"use strict";

/**
 * Sends a page view event to Google Tag Manager
 * @param {string} pageType - the type of the page being viewed
 * @returns {void}
 */
const pageViewEvent = (pageType) => {
    const event = new CustomEvent("pageViewPushed");
    const pageData = document.querySelector(".gtm-page-view-data");
    const userData = document.querySelector(".gtm-user-data");

    if (!pageData || !userData) {
        return;
    }

    const pageDataObject = safeParseJSON(pageData.getAttribute("data-gtm"));
    const userDataObject = safeParseJSON(userData.getAttribute("data-gtm"));

    if (!pageDataObject || !userDataObject) {
        return;
    }

    const object = {
        event: "page_view",
        page_type: pageType,
        ...pageDataObject,
        ...userDataObject
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(object);

    document.dispatchEvent(event);
};

/**
* Sends product data to the data layer for Google Tag Manager events. Used for add_to_cart and remove_from_cart events
* @param {string} productData - JSON string representing product data
* @returns {void}
*/
const cartEvents = (productData) => {
    if (!productData) {
        return;
    }

    const productDataObject = safeParseJSON(productData);

    if (!productDataObject) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(productDataObject);
};

/**
* Represents the view cart event and pushes the view cart object to the data layer
*/
const viewCartEvent = () => {
    const cartPageWrapper = document.querySelector(".cart-page");

    if (!cartPageWrapper) {
        return;
    }

    let viewCartObject = safeParseJSON(cartPageWrapper.getAttribute("data-gtm"));

    if (!viewCartObject) {
        return;
    }

    updateItemIndexes(viewCartObject.ecommerce.items);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(viewCartObject);
};

/**
* Handles the view item event for Google Tag Manager
*/
const viewItemEvent = () => {
    const pdpWrapper = document.querySelector(".product-detail");
    const viewItemObject = safeParseJSON(pdpWrapper.getAttribute("data-gtm"));

    if (!viewItemObject) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(viewItemObject);
};

/**
* Handles the select item event for Google Tag Manager
*/
const selectItemEvent = (wrapper) => {
    if (!wrapper) {
        return;
    }

    const selectItemObject = safeParseJSON(wrapper.getAttribute("data-gtm"));

    if (!selectItemObject) {
        return;
    }

    let eventLocation = "Cart Page";
    if (document.querySelector(".storepage")?.id === "homepage") {
        eventLocation = "Homepage";
    }

    if (document.querySelector(".search-result-count-wrapper")) {
        eventLocation = "Search Results";
    }

    if (document.querySelector(".pdp-container")) {
        eventLocation = "PDP";
    }

    if (wrapper.parentNode.classList.contains("item")) {
        eventLocation = "Search Suggestions";
    }

    if (document.querySelector(".plp-title")) {
        eventLocation = "PLP";
    }

    let objectValue;
    selectItemObject.ecommerce.items.forEach((item) => {
        objectValue = item.price;
        item.item_list_name = eventLocation;
    });

    selectItemObject.event = "select_item";
    selectItemObject.ecommerce.value = objectValue;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(selectItemObject);
};

const recommendationClickEvent = (title, element) => {
    if (!element) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        "event": "pdp_recommendation_click",
        "reco_type": title || "",
        "reco_product_id": element.getAttribute("data-gtm-product-id"),
        "reco_product_name": element.getAttribute("data-gtm-product-name"),
    });
};

/**
 * Safely parses a JSON string and returns the parsed JSON object or null if parsing fails
 * @param {string} str - The JSON string to be parsed
 * @returns {Object | null} The parsed JSON object or null if parsing fails
 */
const safeParseJSON = (str) => {
    try {
        return JSON.parse(str);
    } catch (error) {
        return null;
    }
};

/**
* Handles the order confirmation event for Google Tag Manager
* @param {HTMLElement} wrapper - the HTML element containing the data for the event
* @returns {void}
*/
const purchaseEvent = (orderId) => {
    const orderConfirmationWrapper = document.querySelector(".order-confirmation-wrapper");
    const orderConfirmationObject = safeParseJSON(orderConfirmationWrapper.getAttribute("data-gtm"));

    if (!orderConfirmationObject) {
        return;
    }

    const purchesEventFromDataLayer = window.dataLayer.find(event => event.event === "purchase");

    if (purchesEventFromDataLayer?.ecommerce?.transaction_id === orderId) {
        return false;

    } else {
        updateItemIndexes(orderConfirmationObject.ecommerce.items);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({"ecommerce": null});
        window.dataLayer.push(orderConfirmationObject);
    }
};

/**
* Parses and pushes the page data object to the data layer
* @param {string} data - the page data to be parsed and pushed
* @returns {void}
*/
const checkoutEvents = (data) => {
    if (!data) {
        return;
    }

    const checkoutDataObject = safeParseJSON(data) ? safeParseJSON(data) : data;

    updateItemIndexes(checkoutDataObject.ecommerce.items);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(checkoutDataObject);
};

/**
 * Sorts the interaction event and pushes the data to the data layer
 * @returns {void}
 */
const sortInteractionEvent = () => {
    const gridWrapper = document.querySelector(".product-grid");
    const sortInteractionObject = safeParseJSON(gridWrapper.getAttribute("data-gtm"));

    if (!sortInteractionObject) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(sortInteractionObject);
};

/**
* Updates the indexes of items in the given array
* @param {Array} items - the array of items to be updated
* @returns {void}
*/
const updateItemIndexes = (items) => {
    items.forEach((item, idx) => {
        item.index = idx;
    });
};

/**
* Handles the search event and pushes the search term to the data layer
* @returns {void}
*/
const searchEvent = () => {
    const searchInput = document.querySelector(".search-input").value;

    const searchObject = {
        "event": "user_search",
        "search_term": searchInput
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(searchObject);
};

/**
 * Authenticates customer events and pushes data to the data layer
 * @param {string} data - the customer data to be authenticated
 * @param {string} loginType - the type of login (e.g. social, email)
 * @param {string} isLogin - type of action (login, create)
 * @returns {void}
 */
const authenticateCustomerEvents = (data, loginType, label, isLogin) => {
    if (!data) {
        return;
    }

    const userDataObject = safeParseJSON(data);

    if (!userDataObject) {
        return;
    }

    const object = {
        event: label,
        [isLogin ? "login_type" : "create_type"]: loginType,
        ...userDataObject
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(object);
};

/**
 * Handles form submit events and pushes data to the data layer
 * @param {string} formClass - the class name of the form
 * @param {string} email - submited email address
 * @returns {void}
 */
const formSubmitEvents = (formName, email) => {
    if (!formName) {
        return;
    }

    const object = {
        event: "form_submit",
        form_type: formName
    };

    if (email) {
        object.user = {
            user_em: email
        };
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(object);
};

/**
 * Logs a featured tile click event to the data layer
 * @param {string} tileName - the name of the featured tile being clicked
 * @returns {void}
 */
const featuredTileClick = (tileName) => {
    const dataLayerObject = {
        "event": "featured_tile_click",
        "featured_tile_name": tileName || ""
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerObject);
};

/**
 * Initializes and triggers an IntersectionObserver to observe elements with the class "last-tracked".
 * When an observed element intersects with the viewport, it triggers the `viewItemListEvent` function
 * and then stops observing that element.
 */
function triggerItemListObserver(pageType) {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                viewItemListEvent(pageType);
                observer.unobserve(entry.target);
            }
        });
    });
    const lastTrackedElements = document.querySelectorAll(".last-tracked");
    const lastTrackedElement = lastTrackedElements[lastTrackedElements.length - 1];
    observer.observe(lastTrackedElement);
}

/**
 * Tracks the view item list event for Google Tag Manager
 * @param {string} pageType - the type of page where the event is triggered
 * @param {HTMLElement} targetCarousel - the target carousel element
 */
const viewItemListEvent = (pageType, targetCarousel) => {
    const productGrids = targetCarousel ? targetCarousel : document.querySelectorAll(".product-grid");
    const gridWrapper = productGrids.length > 1 ? productGrids[productGrids.length - 1] : productGrids[0];
    const lastTrackedElements = gridWrapper ? gridWrapper.querySelectorAll(".last-tracked") : false;
    const lastTrackedElement = lastTrackedElements ? lastTrackedElements[lastTrackedElements.length - 1] : false;
    const indexToSlice = lastTrackedElement ? Array.from(gridWrapper.querySelectorAll(".product-tile")).indexOf(lastTrackedElement) + 1 : 0;
    const isOutOfBounds = indexToSlice ? indexToSlice >= gridWrapper.querySelectorAll(".product-tile").length : false;

    if (isOutOfBounds) {
        return;
    }

    const tiles = targetCarousel ?
        targetCarousel.querySelectorAll(".product-tile-pd") :
        Array.from(gridWrapper.querySelectorAll(".product-tile")).slice(indexToSlice, indexToSlice + 10);
    const eventLocation = pageType ? pageType : document.querySelector(".storepage")?.id === "homepage" ? "Homepage" : document.querySelector(".no-results-wrapper") ? "Search Results" : document.querySelector(".pdp-container") ? "PDP" : "Cart Page";

    if (eventLocation === "Cart Page") {
        return;
    }

    let currency = "";
    const dataLayerObject = {
        "event": "view_item_list",
        "ecommerce": {
            "currency": "",
            "items": []
        }
    };

    tiles.forEach((tile, index) => {
        const viewItemObject = safeParseJSON(tile.getAttribute("data-gtm"));
        viewItemObject.ecommerce.items[0].item_list_name = eventLocation;
        dataLayerObject.ecommerce.items.push(viewItemObject.ecommerce.items[0]);
        currency = viewItemObject.ecommerce.currency;
        if (!targetCarousel && index === tiles.length - 1) {
            tile.classList.add("last-tracked");
            triggerItemListObserver(pageType);
        }
    });

    dataLayerObject.ecommerce.currency = currency;
    updateItemIndexes(dataLayerObject.ecommerce.items);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(dataLayerObject);

    $("body").trigger("viewItemListEventComplete");
};

/**
* View promotion event for Google Tag Manager
* @param {HTMLElement} component - the component to trigger the view promotion event
* @returns {void}
*/
const promotionEvents = (type, component) => {
    if (!component) {
        return;
    }

    const isPromotion = component.getAttribute("data-ispromotion");

    if (isPromotion === "false") {
        return;
    }

    const creative_name = component.getAttribute("data-creative-name");
    const creative_slot = component.getAttribute("data-creative-slot");
    const promotion_id = component.getAttribute("data-promotion-id");
    const promotion_name = component.getAttribute("data-promotion-name");

    const dataLayerObject = {
        "event": type,
        "ecommerce": {
            "creative_name": creative_name || "",
            "creative_slot": creative_slot || "",
            "promotion_id": promotion_id || "",
            "promotion_name": promotion_name || "",
            "items": []
        }
    };

    if (component.classList.contains("product-tile-carousel-wrapper")) {
        const eventLocation = document.querySelector(".storepage")?.id === "homepage" ? "Homepage" : document.querySelector(".no-results-wrapper") ? "Search Results" : document.querySelector(".pdp-container") ? "PDP" : "Cart Page";
        const productTiles = component.querySelectorAll(".product-tile-pd");

        productTiles.forEach((tile) => {
            const viewItemObject = safeParseJSON(tile.getAttribute("data-gtm"));
            viewItemObject.ecommerce.items[0].item_list_name = eventLocation;
            dataLayerObject.ecommerce.items.push(viewItemObject.ecommerce.items[0]);
        });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(dataLayerObject);
};

/**
 * Logs a filter interaction event to the data layer
 * @param {string} filterName - the name of the filter being interacted with
 * @param {string} filterValue - the value of the filter being interacted with
 * @returns {void}
 */
const filterInteractionEvent = (filterName, filterValue) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        "event": "filter_interaction",
        "filter_name": filterName || "",
        "filter_value": filterValue || ""
    });
};

/**
* Pushes wishlist view event data to the Google Tag Manager dataLayer.
*
* This function checks for the presence of a wishlist page wrapper element with the class
* ".gtm-page-wish-list". If found, it retrieves and parses the GTM-related JSON data from its
* "data-gtm" attribute. If the data is valid, it pushes a reset event and the wishlist event
* object to the global dataLayer array for GTM tracking.
*
* @function
* @returns {void}
*/
const wishlistViewEvent = () => {
    const wishListPageWrapper = document.querySelector(".gtm-page-wish-list");

    if (!wishListPageWrapper) {
        return;
    }

    let viewWishListObject = safeParseJSON(wishListPageWrapper.getAttribute("data-gtm"));

    if (!viewWishListObject) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({"ecommerce": null});
    window.dataLayer.push(viewWishListObject);
};

module.exports  = {
    pageViewEvent,
    cartEvents,
    checkoutEvents,
    viewCartEvent,
    viewItemEvent,
    selectItemEvent,
    purchaseEvent,
    searchEvent,
    authenticateCustomerEvents,
    sortInteractionEvent,
    viewItemListEvent,
    promotionEvents,
    featuredTileClick,
    formSubmitEvents,
    recommendationClickEvent,
    filterInteractionEvent,
    wishlistViewEvent
};
